import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

// const videos = [
//   {
//     title: "Birthday Celebration",
//     src: [
//       "/videos/video 16.mp4",
//       "/videos/birthday order 2.mp4",
//       "/videos/birthday order 3.mp4",
//     ],
//   },
//   {
//     title: "Children Day",
//     src: ["/videos/video 8.mp4"],
//   },
//   {
//     title: "Valentines Day",
//     src: ["/videos/video 9.mp4"],
//   },
//   {
//     title: "Upanayanam Function",
//     src: ["/videos/video 13.mp4"],
//   },
//   {
//     title: "Sumangali Prarthanai Function",
//     src: ["/videos/video 21.mp4"],
//   },
//   {
//     title: "Marriage Function",
//     src: ["/videos/video 20.mp4"],
//   },
//   // Add more videos here if you have 12 in total, as mentioned
//   // Example:
//   // {
//   //   title: "Additional Video 1",
//   //   src: ["/videos/additional_1.mp4"],
//   // },
//   // {
//   //   title: "Additional Video 2",
//   //   src: ["/videos/additional_2.mp4"],
//   // },
//   // {
//   //   title: "Additional Video 3",
//   //   src: ["/videos/additional_3.mp4"],
//   // },
//   // {
//   //   title: "Additional Video 4",
//   //   src: ["/videos/additional_4.mp4"],
//   // },
//   // {
//   //   title: "Additional Video 5",
//   //   src: ["/videos/additional_5.mp4"],
//   // },
//   // {
//   //   title: "Additional Video 6",
//   //   src: ["/videos/additional_6.mp4"],
//   // },
// ];

const Gallery = () => {
  const [videos, setVideos] = useState([]); // <-- fetched from backend
  const [activeVideoIndex, setActiveVideoIndex] = useState({});
  const [currentCardPage, setCurrentCardPage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(0); // Make responsive

  const API_BASE_URL = "https://a3creativehub.in/api"; // update if deployed

  // ✅ Fetch events dynamically from backend
  const fetchVideos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/events`);
      if (!response.ok) throw new Error(`Failed to fetch events: ${response.status}`);
      const data = await response.json();

      // Transform backend format into Gallery structure
      const formatted = data.map(event => ({
        title: event.titleName,
        src: event.eventVideos || [],
      }));

      setVideos(formatted);
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  // --- responsive cards per page ---

  // Function to determine cards per page based on screen size
  const getCardsPerPage = () => {
    if (window.innerWidth >= 1024) return 6; // Laptops, desktops
    if (window.innerWidth >= 768) return 2;  // Tablets
    return 2; // Mobile
  };

  // Update cardsPerPage on mount and window resize
  useEffect(() => {
    const handleResize = () => {
      setCardsPerPage(getCardsPerPage());
    };

    handleResize(); // Set initial value
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Effect for automatic video swiping within individual cards
  useEffect(() => {
    const intervals = [];

    videos.forEach((video, cardIndex) => {
      const videoSources = Array.isArray(video.src) ? video.src : [video.src];
      const validVideoSources = videoSources.filter(src => src && src.trim() !== "");

      if (validVideoSources.length > 1) {
        const intervalId = setInterval(() => {
          setActiveVideoIndex((prev) => {
            const currentIdx = prev[cardIndex] || 0;
            const nextIdx = (currentIdx + 1) % validVideoSources.length;
            return {
              ...prev,
              [cardIndex]: nextIdx,
            };
          });
        }, 5000);
        intervals.push(intervalId);
      }
    });

    return () => {
      intervals.forEach(clearInterval);
    };
  // eslint-disable-next-line
  }, [videos]);

  // Effect for automatic card pagination (carousel effect for the gallery itself)
  useEffect(() => {
    if (cardsPerPage === 0) return; // Prevent division by zero before cardsPerPage is set

    const totalPages = Math.ceil(videos.length / cardsPerPage);

    if (totalPages <= 1) {
      setCurrentCardPage(0);
      return;
    }

    const cardPaginationInterval = setInterval(() => {
      setCurrentCardPage((prevPage) => (prevPage + 1) % totalPages);
    }, 10000);

    return () => clearInterval(cardPaginationInterval);
    // eslint-disable-next-line
  }, [videos.length, cardsPerPage]);

  // Calculate which cards to display based on currentCardPage
  const startIndex = currentCardPage * cardsPerPage;
  const endIndex = Math.min(startIndex + cardsPerPage, videos.length);
  const cardsToDisplay = videos.slice(startIndex, endIndex);

  // Dynamic card width and margin calculation for max-w
  const getCardWidth = () => {
    if (window.innerWidth >= 1024) return 260;
    if (window.innerWidth >= 768) return 240;
    if (window.innerWidth >= 640) return 220;
    return 200;
  };
  const cardMargin = 8; // mr-2 in Tailwind is typically 8px (0.5rem)

  const calculateVisibleContainerWidth = () => {
    const numCardsOnPage = cardsToDisplay.length;
    if (numCardsOnPage === 0) return '0px';
    return `${numCardsOnPage * getCardWidth() + (numCardsOnPage - 1) * cardMargin}px`;
  };

  const headingVariants = {
    hidden: { opacity: 0, scale: 0.8, y: -20 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
  };

  const galleryCardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const totalVideos = videos.reduce(
    (count, event) => count + (event.src?.length || 0),
    0
  );

  return (
    <section id="gallery" className="bg-gradient-to-r from-pink-200 via-yellow-100 to-pink-100 py-12 pt-16 scroll-mt-16">
      <div className="text-center mb-8">
        <motion.h2
          className="text-3xl font-bold text-pink-600"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.05 }}
        >
          Our Deliveries
          {totalVideos > 0 && (
            <span className="text-pink-600 text-2xl font-bold">
              ({totalVideos})
            </span>
          )}
        </motion.h2>
      </div>

      <div className="flex justify-center">
        <div
          className="relative w-full overflow-hidden transition-all duration-700 ease-in-out"
          style={{ maxWidth: calculateVisibleContainerWidth() }}
        >
          <div className="flex">
            {cardsToDisplay.map((video, indexOnPage) => {
              const originalCardIndex = startIndex + indexOnPage;
              const videoSources = Array.isArray(video.src) ? video.src : [video.src];
              const validVideoSources = videoSources.filter(src => src && src.trim() !== "");

              if (validVideoSources.length === 0) {
                return null;
              }

              const currentVideoIdx = activeVideoIndex[originalCardIndex] || 0;

              return (
                <motion.div
                  key={originalCardIndex}
                  className="flex-shrink-0 w-[200px] sm:w-[220px] md:w-[240px] lg:w-[260px] bg-gradient-to-r from-pink-200 via-yellow-100 to-pink-100 rounded-xl shadow-lg p-1 mr-2 hover:shadow-2xl transition-all duration-300 flex flex-col items-center"
                  variants={galleryCardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.05 }}
                >
                  <div
                    className="relative w-full overflow-hidden rounded-md"
                    style={{ paddingTop: '177.77%' }}
                  >
                    <video
                      key={validVideoSources[currentVideoIdx]}
                      className="absolute top-0 left-0 w-full h-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls={false} // <--- Added this to remove controls
                      preload="auto"
                    >
                      <source src={validVideoSources[currentVideoIdx].replace('.mp4', '.webm')} type="video/webm" />
                      <source src={validVideoSources[currentVideoIdx]} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-800 text-center mt-1 text-wrap">{video.title}</h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Gallery;